import React from 'react';
import { Card, Button, InputGroup, Form as BtForm } from 'react-bootstrap';
import { LuMail, LuLock, LuFacebook, LuTwitter, LuGithub, LuEye, LuEyeOff } from "react-icons/lu";
import { Link, useNavigate } from 'react-router-dom';
import '../../assets/css/auth.css';
import * as Yup from "yup";
import { Formik, Form, ErrorMessage } from "formik";
import { logIn } from '../../redux/reducers/authSlice';
import { useDispatch, useSelector } from 'react-redux';
import Spinner from '../../components/Spinner';

const Login = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { loading } = useSelector((state) => state.auth);

    const [showPassword, setShowPassword] = React.useState(false);

    const initialValues = {
        email: "",
        password: "",
        rememberMe: false
    };

    const validationSchema = Yup.object({
        email: Yup.string()
            .email("Please enter a valid email address")
            .required("Email is required"),
        password: Yup.string().required("Password is required"),
    });

    const onSubmit = (values, { setFieldError }) => {
        dispatch(logIn(values)).then((res) => {
            const responseData = res.payload;

            if (responseData?.token) {
                const user = responseData.user;
                if (!user.isVerified) {
                    navigate("/onboarding/verify-notice");
                } else if (!user.hasCompletedOnboarding || user.onboardingStep < 2) {
                    navigate("/onboarding/setup-business");
                } else {
                    navigate("/");
                }
            } else {
                setFieldError("email", "Invalid credentials");
            }
        });
    };

    return (
        <div className="auth-wrapper d-flex align-items-center justify-content-center bg-light px-3" style={{ minHeight: '100vh' }}>
            <Card className="auth-card border-0 shadow-lg rounded-4 overflow-visible position-relative p-3 p-md-4" style={{ maxWidth: '450px', width: '100%' }}>

                <div className="auth-logo-badge position-absolute top-0 start-50 translate-middle">
                    <div className="bg-brand rounded-circle d-flex align-items-center justify-content-center text-white fw-bold" style={{ width: '60px', height: '60px', fontSize: '24px' }}>
                        S
                    </div>
                </div>

                <div className="text-center mt-4 mb-4">
                    <h3 className="fw-bold text-dark">Login</h3>
                    <p className="text-muted small px-3">
                        Thank you for coming back.
                        <br />Let's access your Business Information.
                    </p>
                </div>

                <div className="position-relative">
                    <Spinner active={loading}>
                        <Formik
                            initialValues={initialValues}
                            validationSchema={validationSchema}
                            onSubmit={onSubmit}
                        >
                            {(formik) => (
                                <Form>
                                    <BtForm.Group className="mb-3 text-start">
                                        <BtForm.Label className="small fw-bold">Email Address</BtForm.Label>
                                        <InputGroup className={`rounded-3 border ${formik.errors.email && formik.touched.email ? 'border-danger' : ''}`}>
                                            <InputGroup.Text className="bg-transparent border-0 pe-0 text-muted">
                                                <LuMail size={18} />
                                            </InputGroup.Text>
                                            <BtForm.Control
                                                type="email"
                                                name="email"
                                                placeholder="admin@example.com"
                                                className="bg-transparent border-0 py-2 shadow-none"
                                                {...formik.getFieldProps('email')}
                                                disabled={loading}
                                            />
                                        </InputGroup>
                                        <div className="text-danger small mt-1">
                                            <ErrorMessage name="email" />
                                        </div>
                                    </BtForm.Group>

                                    <BtForm.Group className="mb-3 text-start">
                                        <BtForm.Label className="small fw-bold">Password</BtForm.Label>
                                        <InputGroup className={`rounded-3 border ${formik.errors.password && formik.touched.password ? 'border-danger' : ''}`}>
                                            <InputGroup.Text className="bg-transparent border-0 pe-0 text-muted">
                                                <LuLock size={18} />
                                            </InputGroup.Text>
                                            <BtForm.Control
                                                type={showPassword ? "text" : "password"}
                                                name="password"
                                                placeholder="••••••"
                                                className="bg-transparent border-0 py-2 shadow-none"
                                                {...formik.getFieldProps('password')}
                                                disabled={loading}
                                            />
                                            <Button 
                                                variant="link" 
                                                className="text-muted border-0 shadow-none pe-3" 
                                                onClick={() => setShowPassword(!showPassword)}
                                                disabled={loading}
                                            >
                                                {showPassword ? <LuEyeOff size={18} /> : <LuEye size={18} />}
                                            </Button>
                                        </InputGroup>
                                        <div className="text-danger small mt-1">
                                            <ErrorMessage name="password" />
                                        </div>
                                    </BtForm.Group>

                                    <div className="d-flex justify-content-between align-items-center mb-4">
                                        <BtForm.Check
                                            type="checkbox"
                                            id="remember-me"
                                            name="rememberMe"
                                            label={<span className="small text-muted">Remember Me</span>}
                                            {...formik.getFieldProps('rememberMe')}
                                            checked={formik.values.rememberMe}
                                            disabled={loading}
                                        />
                                        <Link to="/forgot-password" global="true" className="text-brand text-decoration-none small fw-bold">
                                            Forgot password?
                                        </Link>
                                    </div>

                                    <Button
                                        variant="brand"
                                        type="submit"
                                        className="btn btn-primary w-100 py-2 fw-bold mb-4 shadow-sm btn-brand"
                                        disabled={loading}
                                    >
                                        {loading ? 'LOGGING IN...' : 'LOGIN'}
                                    </Button>

                                    <div className="position-relative text-center mb-4">
                                        <hr className="text-muted opacity-25" />
                                        <span className="position-absolute top-50 start-50 translate-middle bg-white px-3 text-muted small fw-bold">OR</span>
                                    </div>

                                    <div className="d-flex gap-2 justify-content-center mb-4">
                                        <Button variant="outline-light" className="border flex-grow-1 text-dark shadow-sm py-2" disabled={loading}>
                                            <LuFacebook size={20} />
                                        </Button>
                                        <Button variant="outline-light" className="border flex-grow-1 text-dark shadow-sm py-2" disabled={loading}>
                                            <LuTwitter size={20} />
                                        </Button>
                                        <Button variant="outline-light" className="border flex-grow-1 text-dark shadow-sm py-2" disabled={loading}>
                                            <LuGithub size={20} />
                                        </Button>
                                    </div>

                                    <p className="text-center text-muted small mb-0">
                                        Don't have an account? <Link to="/register" className="text-brand text-decoration-none fw-bold">Create an Account</Link>
                                    </p>
                                </Form>
                            )}
                        </Formik>
                    </Spinner>
                </div>
            </Card>
        </div>
    );
};

export default Login;
